import { FastField, Form, Formik } from 'formik';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExclamationCircle } from 'react-icons/all';
import { useDispatch, useSelector } from 'react-redux';
import { UserProfile } from '../../../../@posturinn/client-react';
import { closeAllModals } from '../../../../actions/modalActions';
import { addEmail } from '../../../../actions/userActions';
import { updateUserProfile } from '../../../../actions/userProfileActions';
import { validateEmail } from '../../../../helpers/formHelpers/formHelpers';
import { ReactComponent as Close } from '../../../../icons/close-button.svg';
import IUserProfileReducer from '../../../../interfaces/reducers/userProfileReducer';
import { IStore } from '../../../../reducers';
import Button from '../../../base/button/button';
import FormikFieldWrapper from '../../../base/forms/formikFieldWrapper';
import { Input, StyledFormikField } from '../../../base/forms/input';
import { A } from '../../../base/typography';
import { RawLoader } from '../../../loading/loading';
import * as StyledStepInfoForm from '../../../registerShipment/registerShipmentForm/stepInfo/stepInfoForm/stepInfoForm.styles';
import * as StyledModals from '../myShipmentsModals.styles';
import { myProfileInitialValues, myProfileSchema } from './myProfile.schema';

interface Props {}

const MyProfile: FC<Props> = () => {
	const { t } = useTranslation(['myShipments', 'registerShipment']);
	const dispatch = useDispatch<any>();
	const [emailWarning, setEmailWarning] = useState(null);

	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { userProfile, modalSubmitPending, modalSubmitError, modalSubmitErrorDetails } = userProfileReducer;

	const { modalHeading } = useSelector(({ modalReducer }) => modalReducer);

	const hideIcelandicCountryCode = phoneNumber => {
		if (phoneNumber && phoneNumber.length === 11 && phoneNumber.substring(0, 4) === '+354')
			return phoneNumber.substring(4);
		return phoneNumber;
	};

	if (!userProfile) return <RawLoader />;

	myProfileInitialValues.email = userProfile.email || '';
	myProfileInitialValues.phoneNumber = hideIcelandicCountryCode(userProfile.phoneNumber) || '';

	const closeModal = () => {
		dispatch(closeAllModals());
		document.body.style.overflow = 'unset';
	};
	document.body.style.overflow = 'hidden';

	const handleEmailBlur = async (field: any, e: any) => {
		field.onBlur(e);
		await handleEmailValidation(e.currentTarget.value);
	};

	const handleEmailChange = async (field: any, e: any) => {
		field.onChange(e);
		await handleEmailValidation(e.currentTarget.value);
	};

	const handleEmailValidation = async (value: any) => {
		const warningMessage = await validateEmail(value, t, 'registerShipment');
		setEmailWarning(warningMessage);
	};

	return (
		<StyledModals.Wrapper>
			<StyledModals.Heading>
				<div>{t('page-settings.changeProfile')}</div>
				<A onClick={closeModal}>
					<Close />
				</A>
			</StyledModals.Heading>

			{modalSubmitError && (
				<StyledModals.ErrorMessage>
					<FaExclamationCircle />
					<div>{modalSubmitErrorDetails !== '' ? modalSubmitErrorDetails : t('genericError')}</div>
				</StyledModals.ErrorMessage>
			)}

			<Formik
				initialValues={myProfileInitialValues}
				validationSchema={myProfileSchema}
				onSubmit={values => {
					const newUserProfile: UserProfile = userProfile.clone();
					newUserProfile.email = values.email;
					newUserProfile.phoneNumber = values.phoneNumber;

					if (modalHeading === 'EMAIL')
						dispatch(addEmail(userProfileReducer.userProfile.userId, values.email, newUserProfile));
					else dispatch(updateUserProfile(newUserProfile));
				}}>
				<Form>
					{modalHeading === 'EMAIL' && (
						<FormikFieldWrapper label={t('email')} name="email" languageComponent="myShipments" smallLabel>
							<FastField id="email" name="email">
								{({ field }) => (
									<Input
										type="text"
										id={field.id}
										name={field.name}
										value={field.value}
										onBlur={(e: any) => handleEmailBlur(field, e)}
										onChange={(e: any) => handleEmailChange(field, e)}
										placeholder={t('email')}
									/>
								)}
							</FastField>
							{emailWarning && (
								<StyledStepInfoForm.WarningMessage>{t(emailWarning)}</StyledStepInfoForm.WarningMessage>
							)}
						</FormikFieldWrapper>
					)}

					{modalHeading === 'PHONE' && (
						<FormikFieldWrapper
							label={t('phone')}
							name="phoneNumber"
							languageComponent="myShipments"
							smallLabel>
							<StyledFormikField
								type="text"
								id="phoneNumber"
								name="phoneNumber"
								placeholder={t('phone')}
							/>
						</FormikFieldWrapper>
					)}

					<Button primary type="submit" isLoading={modalSubmitPending}>
						{t('save')}
					</Button>
				</Form>
			</Formik>
		</StyledModals.Wrapper>
	);
};

export default MyProfile;
